import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap, Plus, Pencil, Trash2, Layers } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr, num, fmtDate, initials } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { NewDPDialog } from "@/pages/Onboarding";
import { DPWelcomeKitDialog } from "@/components/DPWelcomeKitDialog";
import { EditDPDialog } from "@/components/EditDPDialog";
import { OnboardingSuccessDialog } from "@/components/certs/OnboardingSuccessDialog";
import { successFromDp } from "@/lib/certs";

export default function DeliveryPartners() {
  const [dps, setDps] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [location, setLocation] = useState("all");
  const [locs, setLocs] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [kitDp, setKitDp] = useState(null);
  const [done, setDone] = useState(null);
  const [editDp, setEditDp] = useState(null);
  const [delDp, setDelDp] = useState(null);
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkModel, setBulkModel] = useState("");
  const [models, setModels] = useState([]);
  const nav = useNavigate();
  const { openQuickEntry, role, can } = useApp();
  const canManage = can("dp.edit") || can("dp.delete");

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status !== "all") params.set("status", status);
    if (location !== "all") params.set("location", location);
    api.get(`/dp?${params}`).then(({ data }) => setDps(data));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [q, status, location]); // eslint-disable-line
  useEffect(() => { api.get("/locations").then(({ data }) => setLocs(data)); api.get("/earning-models/active").then(({ data }) => setModels(data)).catch(() => {}); }, []);

  const confirmDelete = async () => {
    try {
      await api.delete(`/dp/${delDp.id}`);
      toast.success(`${delDp.full_name} removed`);
      setDelDp(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const toggleSel = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected((s) => s.length === dps.length ? [] : dps.map((d) => d.id));
  const doBulkAssign = async () => {
    if (!bulkModel) { toast.error("Select an earning model"); return; }
    try {
      const { data } = await api.post("/earning-models/bulk-assign", { dp_ids: selected, model_id: bulkModel });
      toast.success(`Updated ${data.updated} partner(s) to ${data.model}`);
      setBulkOpen(false); setSelected([]); setBulkModel(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const dupFe = new Set();
  const dupMob = new Set();
  const seenFe = {}; const seenMob = {};
  dps.forEach((d) => {
    if (d.fe_id) { if (seenFe[d.fe_id]) dupFe.add(d.fe_id); else seenFe[d.fe_id] = 1; }
    if (d.mobile) { if (seenMob[d.mobile]) dupMob.add(d.mobile); else seenMob[d.mobile] = 1; }
  });

  return (
    <div>
      <PageHeader title="Delivery Partners" subtitle={`${dps.length} partners`}>
        {can("dp.add") && <Button onClick={() => setNewOpen(true)} data-testid="add-dp-btn"><Plus className="mr-1 h-4 w-4" /> Onboard DP</Button>}
      </PageHeader>

      <div className="mb-4 flex flex-wrap gap-2">
        <Input placeholder="Search name / FE ID / mobile…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="dp-list-search" />
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-36" data-testid="dp-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem><SelectItem value="Suspended">Suspended</SelectItem></SelectContent></Select>
        <Select value={location} onValueChange={setLocation}><SelectTrigger className="w-40" data-testid="dp-location-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Locations</SelectItem>{locs.map((l) => <SelectItem key={l.name} value={l.name}>{l.name}</SelectItem>)}</SelectContent></Select>
      </div>

      <div className="overflow-hidden rounded-xl border border-border bg-card">
        {canManage && selected.length > 0 && (
          <div className="flex items-center justify-between gap-2 border-b border-border bg-primary/5 px-4 py-2" data-testid="bulk-bar">
            <p className="text-sm"><span className="font-bold text-primary">{selected.length}</span> selected</p>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => setBulkOpen(true)} data-testid="bulk-change-model"><Layers className="mr-1.5 h-3.5 w-3.5" /> Change Earning Model</Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected([])}>Clear</Button>
            </div>
          </div>
        )}
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            {canManage && <TableHead className="w-8"><input type="checkbox" checked={selected.length === dps.length && dps.length > 0} onChange={toggleAll} onClick={(e) => e.stopPropagation()} className="h-4 w-4 accent-primary" data-testid="select-all-dp" /></TableHead>}
            <TableHead>Partner</TableHead><TableHead>FE ID</TableHead><TableHead>Location</TableHead>
            <TableHead>Earning Model</TableHead>
            <TableHead className="text-right">Today</TableHead><TableHead className="text-right">Month Orders</TableHead>
            <TableHead className="text-right">Month Earnings</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {dps.map((d) => {
              const isDup = dupFe.has(d.fe_id) || (d.mobile && dupMob.has(d.mobile));
              return (
              <TableRow key={d.id} className="cursor-pointer" data-testid={`dp-row-${d.fe_id}`} onClick={() => nav(`/dp/${d.id}`)}>
                {canManage && <TableCell onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.includes(d.id)} onChange={() => toggleSel(d.id)} className="h-4 w-4 accent-primary" data-testid={`select-dp-${d.fe_id}`} /></TableCell>}
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">{initials(d.full_name)}</div>
                    <div>
                      <p className="font-semibold">{d.full_name}{isDup && <span className="ml-2 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-rose-500" data-testid={`dup-badge-${d.fe_id}`}>DUPLICATE</span>}</p>
                      <p className="text-xs text-muted-foreground">{d.mobile} · {d.platform}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-xs">{d.fe_id}</TableCell>
                <TableCell>{d.location}</TableCell>
                <TableCell className="text-xs">{d.earning_model_name ? <span className="rounded bg-primary/10 px-1.5 py-0.5 text-primary">{d.earning_model_name}</span> : <span className="text-muted-foreground">Global slabs</span>}</TableCell>
                <TableCell className="text-right tabular">{num(d.stats?.today_orders)}</TableCell>
                <TableCell className="text-right tabular">{num(d.stats?.month_orders)}</TableCell>
                <TableCell className="text-right tabular font-semibold text-emerald-500">{inr(d.stats?.month_earnings)}</TableCell>
                <TableCell><StatusBadge status={d.status} /></TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-0.5">
                    <Button size="sm" variant="ghost" onClick={() => openQuickEntry(d)} data-testid={`quick-entry-${d.fe_id}`} title="Add daily activity" className="h-8 w-8 p-0"><Zap className="h-4 w-4 text-amber-500" /></Button>
                    {canManage && <Button size="sm" variant="ghost" onClick={() => setEditDp(d)} data-testid={`edit-dp-${d.fe_id}`} title="Edit details" className="h-8 w-8 p-0"><Pencil className="h-4 w-4" /></Button>}
                    {canManage && <Button size="sm" variant="ghost" onClick={() => setDelDp(d)} data-testid={`delete-dp-${d.fe_id}`} title="Delete partner" className="h-8 w-8 p-0 text-rose-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            );})}
          </TableBody>
        </Table>
      </div>

      <NewDPDialog open={newOpen} onClose={() => setNewOpen(false)} onSaved={(c) => { load(); if (c?.fe_id) setDone({ ...successFromDp(c), raw: c }); }} locs={locs} />
      <OnboardingSuccessDialog partner={done} onClose={() => setDone(null)} onWelcomeKit={() => { setKitDp(done.raw); setDone(null); }} />
      <DPWelcomeKitDialog dp={kitDp} onClose={() => setKitDp(null)} />
      <EditDPDialog dp={editDp} open={!!editDp} onClose={() => setEditDp(null)} onSaved={load} locs={locs} />

      <Dialog open={bulkOpen} onOpenChange={(o) => !o && setBulkOpen(false)}>
        <DialogContent className="bg-card" data-testid="bulk-model-dialog">
          <DialogHeader><DialogTitle className="font-head">Change Earning Model — {selected.length} partner(s)</DialogTitle></DialogHeader>
          <Select value={bulkModel} onValueChange={setBulkModel}>
            <SelectTrigger data-testid="bulk-model-select"><SelectValue placeholder="Select active model" /></SelectTrigger>
            <SelectContent>{models.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">Historical earnings are NOT recalculated — only future daily entries use the new model.</p>
          <DialogFooter><Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button><Button onClick={doBulkAssign} disabled={!bulkModel} data-testid="confirm-bulk-model">Apply to {selected.length} partner(s)</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delDp} onOpenChange={(o) => !o && setDelDp(null)}>
        <AlertDialogContent data-testid="delete-dp-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Delivery Partner?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes <span className="font-semibold text-foreground">{delDp?.full_name}</span> ({delDp?.fe_id}) and all their daily orders, attendance, incentives, assets and EV records. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-dp">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-dp" className="bg-rose-600 hover:bg-rose-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
